import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ActionNotice from "@/components/base/ActionNotice";
import { usePageMeta } from "@/hooks/usePageMeta";
import { communityTopics, type TopicReply } from "@/mocks/topics";
import { formatDate } from "@/utils/format";

export default function CommunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const topic = communityTopics.find((item) => item.id === id);
  const [replies, setReplies] = useState<TopicReply[]>(topic ? topic.replies : []);
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setReplies(topic ? topic.replies : []);
    setDraft("");
  }, [id, topic]);

  usePageMeta({
    title: topic ? `${topic.title} - 社区话题 - 成人网` : "社区话题详情 - 成人网",
    description: topic
      ? `${topic.excerpt.slice(0, 80)} 来自成人网社区“${topic.category}”板块的讨论，已有 ${topic.replyCount} 条回复。`
      : "成人网社区话题详情页，浏览讨论内容并参与回复演示。",
    keywords: topic ? `${topic.title},${topic.category},成人社区,话题讨论` : "成人社区,话题详情",
  });

  const related = useMemo(() => {
    if (!topic) {
      return communityTopics.slice(0, 3);
    }
    return communityTopics.filter((item) => item.id !== topic.id).slice(0, 3);
  }, [topic]);

  if (!topic) {
    return (
      <div className="container-page py-10">
        <EmptyState
          icon="ri-chat-3-line"
          title="没有找到这个话题"
          description="话题可能已被删除，或者链接有误。可以回到社区列表看看其他讨论。"
          actionLabel="返回社区"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/community");
          }}
        />
      </div>
    );
  }

  const submitReply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content) {
      return;
    }
    const reply: TopicReply = {
      id: `local-${Date.now()}`,
      author: "访客（演示）",
      time: "刚刚",
      content,
    };
    setReplies((prev) => [reply, ...prev]);
    setDraft("");
    setNotice("回复已添加到本页（前端演示，刷新后消失）");
  };

  return (
    <div className="container-page py-7">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "社区话题", to: "/community" },
          { label: topic.title },
        ]}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <article className="panel p-5 md:p-6">
            <span className="rounded-sm bg-primary-500/20 px-2 py-0.5 text-[11px] text-accent-500">
              {topic.category}
            </span>
            <h1 className="mt-3 font-heading text-[24px] leading-snug text-foreground-950 md:text-[30px]">
              {topic.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-3 border-b border-background-200 pb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-xs text-accent-500">
                {topic.author.slice(0, 1)}
              </div>
              <div>
                <p className="text-xs text-foreground-900">{topic.author}</p>
                <p className="mt-0.5 text-[10.5px] text-foreground-500">{topic.authorBio}</p>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {topic.createdAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-1-line"></i>
                  {topic.replyCount + (replies.length - topic.replies.length)} 回复
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-team-line"></i>
                  {topic.participantCount} 参与
                </span>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              {topic.content.map((paragraph, index) => (
                <p
                  key={`${topic.id}-c-${index}`}
                  className="text-[13.5px] leading-[1.95] text-foreground-600"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-5 overflow-hidden rounded-lg border border-background-200">
              <img
                src={topic.image}
                alt={`${topic.title} - 话题配图`}
                title={`${topic.title} 社区话题配图`}
                className="h-[200px] w-full object-cover md:h-[300px]"
              />
              <p className="bg-background-100 px-3 py-2 text-[10.5px] text-foreground-500">
                示意图 · 与话题内容相关的演示配图
              </p>
            </div>
          </article>

          <section className="mt-6">
            <h2 className="mb-3 text-base text-foreground-950">
              全部回复{" "}
              <span className="text-xs text-foreground-500">
                （{replies.length} 条，含演示回复）
              </span>
            </h2>
            <div className="panel divide-y divide-background-200">
              {replies.map((reply) => (
                <div key={reply.id} className="flex gap-3 p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-200 text-[11px] text-accent-500">
                    {reply.author.slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                      <span className="text-foreground-800">{reply.author}</span>
                      <span className="text-foreground-500">{reply.time}</span>
                      {reply.time === "刚刚" ? (
                        <span className="rounded-sm bg-accent-500/15 px-1.5 py-0.5 text-[10px] text-accent-500">
                          未保存
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
                      {reply.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-6 panel p-4 md:p-5">
            <h2 className="text-sm font-medium text-foreground-950">添加回复（演示）</h2>
            <p className="mt-1.5 text-[11px] text-foreground-500">
              回复只会在当前页面显示，不会上传或保存。请遵守社区准则，不要填写个人信息。
            </p>
            <form onSubmit={submitReply} className="mt-3">
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                rows={4}
                maxLength={500}
                placeholder="说说你的看法…（最多 500 字）"
                aria-label="回复内容"
                className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-600 focus:border-accent-500 focus:outline-none"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10.5px] text-foreground-600">{draft.length} / 500</span>
                <button
                  type="submit"
                  className="flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line"></i>
                  提交回复
                </button>
              </div>
            </form>
          </section>
        </div>

        <aside className="min-w-0 space-y-5">
          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">话题信息</h2>
            <dl className="mt-3 space-y-2.5 text-xs">
              {[
                { label: "板块", value: topic.category },
                { label: "发起人", value: topic.author },
                { label: "发起时间", value: formatDate(topic.createdAt.slice(0, 10)) },
                { label: "最后回复", value: formatDate(topic.lastReplyAt.slice(0, 10)) },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
            <Link
              to="/community"
              className="mt-4 flex items-center justify-center gap-1.5 rounded-md border border-background-300 py-2 text-[11px] text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
            >
              <i className="ri-arrow-left-line"></i>
              返回社区列表
            </Link>
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">其他讨论</h2>
            <div className="mt-3 space-y-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/community-detail/${item.id}`}
                  className="group block border-b border-background-200/70 pb-3 last:border-b-0 last:pb-0"
                >
                  <p className="line-clamp-2-safe text-xs text-foreground-900 transition-colors group-hover:text-accent-500">
                    {item.title}
                  </p>
                  <p className="mt-1 text-[10px] text-foreground-500">
                    {item.replyCount} 回复 · {item.author}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>

      {notice ? <ActionNotice message={notice} onClose={() => setNotice("")} /> : null}
    </div>
  );
}